import axios, { AxiosResponse } from 'axios';
import $api from '@/http/index';
import type { IField } from '@models/IFormData';

export interface IDirectWriteLockInfo {
    keeperUserId?: string;
    keeperUserName?: string;
    lockStartTime?: string;
}

/** Почему сервер не выполнил запись: документ занят или изменился после того, как значения показали. */
export type DirectWriteConflict = 'lock' | 'changed';

export interface IDirectWriteRequest {
    fields: IField[];
    reason: string;
    source: 'debug-panel' | 'support-ui';
    runHooks: boolean;
    raiseModifyEvents: boolean;
    force: boolean;
    /** `doc_Hash`, при котором показаны значения «было»; не совпал на сервере — 409 `changed`. */
    expectedHash?: string;
}

export interface IDirectWriteResult {
    id: string;
    success: boolean;
    message?: string;
    lockInfo?: IDirectWriteLockInfo;
    conflict?: DirectWriteConflict;
    currentHash?: string;
}

export interface IDirectWriteFailure {
    status?: number;
    message: string;
    lockInfo?: IDirectWriteLockInfo;
    conflict?: DirectWriteConflict;
    /** `doc_Hash` документа на сервере при отказе `changed`. */
    currentHash?: string;
}

export class DocumentDebugService {
    static writeFields(
        docId: string,
        request: IDirectWriteRequest,
    ): Promise<AxiosResponse<IDirectWriteResult>> {
        return $api.patch<IDirectWriteResult>(`/document/${encodeURIComponent(docId)}/fields`, request, {
            suppressErrorNotification: true,
        });
    }

    /** Причина отказа из ошибки axios или из уже разобранного отказа (его бросают обёртки записи). */
    static getFailure(error: unknown): IDirectWriteFailure {
        if (!axios.isAxiosError(error)) {
            if (typeof error === 'object' && error !== null && 'message' in error) {
                const { status, message, lockInfo, conflict, currentHash } = error as IDirectWriteFailure;
                return { status, message: String(message), lockInfo, conflict, currentHash };
            }
            return { message: String(error) };
        }

        const data = error.response?.data as Partial<IDirectWriteResult> | undefined;
        return {
            status: error.response?.status,
            message: data?.message || error.message || 'Не удалось записать поле',
            lockInfo: data?.lockInfo,
            conflict: data?.conflict,
            currentHash: data?.currentHash,
        };
    }
}
